import React from 'react'
import { Box, Button, Text } from '@chakra-ui/react'
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
} from '@chakra-ui/react'
import { ClientPhoneFieldsFragment } from '~graphql/generated/graphql'
import { getPhoneCountryFromCode } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'

interface Props {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    phone: ClientPhoneFieldsFragment | undefined
    defaultPhone: ClientPhoneFieldsFragment | undefined
    clinicCountry?: string | null
}

export default function SendToUpdateAlert({
    isOpen,
    onClose,
    onConfirm,
    phone,
    defaultPhone,
    clinicCountry,
}: Props) {
    const cancelRef = React.useRef(null)
    const formattedPhone =
        formatPhoneReadable(
            phone?.phone,
            phone?.country_code
                ? getPhoneCountryFromCode(phone.country_code)
                : clinicCountry,
        ) || phone?.phone
    const phoneName = phone?.name
    const formattedDefaultPhone =
        formatPhoneReadable(
            defaultPhone?.phone,
            defaultPhone?.country_code
                ? getPhoneCountryFromCode(defaultPhone.country_code)
                : clinicCountry,
        ) || defaultPhone?.phone
    const defaultPhoneName = defaultPhone?.name

    return (
        <AlertDialog
            size="xl"
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            leastDestructiveRef={cancelRef}
        >
            <AlertDialogOverlay />
            <AlertDialogContent rounded="2xl">
                <AlertDialogHeader fontSize="md">
                    Continue messaging {formattedPhone}{' '}
                    {phoneName ? `(${phoneName})` : ''}?
                </AlertDialogHeader>
                <AlertDialogBody
                    borderTopWidth={1}
                    borderTopColor="gray.200"
                    py={2}
                >
                    <Text fontSize="sm">
                        We detected previous communications with{' '}
                        <Box as="span" fontWeight="bold">
                            {formattedPhone}
                        </Box>{' '}
                        {phoneName ? `(${phoneName})` : ''}. The primary phone
                        number for this client is{' '}
                        <Box as="span" fontWeight="bold">
                            {formattedDefaultPhone}
                        </Box>{' '}
                        {defaultPhoneName ? `(${defaultPhoneName})` : ''}. Would
                        you like to continue messaging this number or use the
                        primary number?
                    </Text>
                </AlertDialogBody>
                <AlertDialogFooter justifyContent="space-between">
                    <Button
                        ref={cancelRef}
                        onClick={onClose}
                        size="sm"
                        rounded="full"
                    >
                        No, use the primary number
                    </Button>
                    <Button
                        colorScheme="brand"
                        onClick={onConfirm}
                        size="sm"
                        rounded="full"
                    >
                        Yes, use this number
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
